// src/pages/mlb/MlbSimulations.jsx
import React, { useEffect, useMemo, useState } from "react";

/** =========================
 * CONFIG
 * ========================= */
const BASE = "/data/mlb/latest";
const FILES = {
  batter: `${BASE}/batter_sim_results.json`,
  pitcher: `${BASE}/pitcher_sim_results.json`,
  pitcherData: `${BASE}/pitcher_data.json`,
  headshots: `${BASE}/headshots.json`, // optional manifest
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

/** =========================
 * FETCH / BASIC HELPERS
 * ========================= */
async function fetchJson(url) {
  const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
  const txt = await res.text();
  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || /^[\[{]/.test(txt.trim());
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  try {
    return JSON.parse(txt);
  } catch {
    throw new Error(`Invalid JSON @ ${url}`);
  }
}

async function fetchOptionalJson(url) {
  try {
    return await fetchJson(url);
  } catch {
    return [];
  }
}

function isNum(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function toNum(v) {
  if (v == null || v === "") return null;
  if (isNum(v)) return v;
  const n = Number(String(v).replace(/[,%$\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function fmt1(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isNum(v)) return Number(v).toFixed(1);
  return String(v);
}

function fmt2(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isNum(v)) return Number(v).toFixed(2);
  return String(v);
}

function fmt3(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isNum(v)) return Number(v).toFixed(3);
  return String(v);
}

function toPct(v, digits = 1) {
  if (!isNum(v)) return "";
  const p = v <= 1.0000001 ? v : v / 100;
  return `${(p * 100).toFixed(digits)}%`;
}

function probToAmericanOdds(p) {
  if (!isNum(p)) return "";
  if (p <= 0) return "";
  if (p >= 1) return "-∞";
  const prob = p <= 1.0000001 ? p : p / 100;
  if (prob >= 0.5) {
    const odds = -Math.round((prob / (1 - prob)) * 100);
    return String(odds);
  }
  const odds = Math.round(((1 - prob) / prob) * 100);
  return `+${odds}`;
}

function exportToCsv(filename, rows, columns) {
  const header = columns.join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const raw = row[col];
          const cell = raw == null ? "" : String(raw);
          return `"${cell.replaceAll('"', '""')}"`;
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** =========================
 * NAME / TEAM / HEADSHOT HELPERS
 * ========================= */
const teamLogo = (team) => `/logos/mlb/${String(team || "").toUpperCase()}.png`;

function normalizeName(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(jr|sr|ii|iii|iv)\b\.?/gi, "")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizeTeam(s) {
  return String(s || "").trim().toUpperCase();
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("");
}

function levenshtein(a, b) {
  const s = normalizeName(a);
  const t = normalizeName(b);
  const m = s.length;
  const n = t.length;
  if (!m) return n;
  if (!n) return m;

  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

function similarity(a, b) {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  const maxLen = Math.max(na.length, nb.length, 1);
  return 1 - levenshtein(na, nb) / maxLen;
}

function fuzzyIncludes(hay, needle) {
  const h = normalizeName(hay);
  const n = normalizeName(needle);
  if (!n) return true;
  if (h.includes(n)) return true;

  const hayTokens = h.split(" ").filter(Boolean);
  const needleTokens = n.split(" ").filter(Boolean);
  if (!needleTokens.length) return true;

  let matched = 0;
  for (const nt of needleTokens) {
    if (hayTokens.some((ht) => ht.includes(nt) || nt.includes(ht))) matched += 1;
  }
  return matched >= Math.max(1, Math.ceil(needleTokens.length * 0.6));
}

function guessTeamKeyFromRow(row) {
  if (!row) return null;
  const keys = Object.keys(row);
  const candidates = ["Team", "team", "Tm", "tm", "TEAM"];
  for (const c of candidates) if (keys.includes(c)) return c;
  return keys.find((k) => k.toLowerCase().includes("team")) || null;
}

function pickPlayerKey(rows) {
  if (!rows?.length) return "Player";
  const keys = Object.keys(rows[0] || {});
  const candidates = ["Player", "player", "Name", "name"];
  for (const c of candidates) if (keys.includes(c)) return c;
  return keys.find((k) => k.toLowerCase().includes("player")) || keys.find((k) => k.toLowerCase().includes("name")) || "Player";
}

function buildHeadshotIndex(manifest) {
  const list = Array.isArray(manifest) ? manifest : [];
  return list.map((x) => ({
    raw: x,
    name: String(x?.name || x?.player || x?.Player || x?.Name || ""),
    team: normalizeTeam(x?.team || x?.Team || x?.tm || x?.Tm || ""),
    src: x?.src || x?.path || x?.url || "",
    norm: normalizeName(x?.name || x?.player || x?.Player || x?.Name || ""),
  }));
}

function findHeadshotSrc({ player, team, headshotIndex }) {
  const p = String(player || "");
  const t = normalizeTeam(team || "");
  if (!p || !headshotIndex?.length) return "";

  const exactTeam = headshotIndex.filter((x) => x.team === t);
  const pool = exactTeam.length ? exactTeam : headshotIndex;

  let best = null;
  let bestScore = -Infinity;

  for (const item of pool) {
    const sim = similarity(p, item.name);
    const teamBoost = item.team === t ? 0.08 : 0;
    const score = sim + teamBoost;

    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  if (best && bestScore >= 0.72) return best.src || "";
  return "";
}

/** =========================
 * DATA NORMALIZATION
 * ========================= */
function addDerivedProjectedHits(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((r) => {
    const out = { ...r };
    const keys = Object.keys(out);
    const findKey = (re) => keys.find((k) => re.test(String(k)));

    const k1b = findKey(/^(projected\s*)?1b$/i) || findKey(/\bprojected\s*1b\b/i) || findKey(/^1b\b/i);
    const k2b = findKey(/^(projected\s*)?2b$/i) || findKey(/\bprojected\s*2b\b/i) || findKey(/^2b\b/i);
    const k3b = findKey(/^(projected\s*)?3b$/i) || findKey(/\bprojected\s*3b\b/i) || findKey(/^3b\b/i);
    const khr = findKey(/^(projected\s*)?hr$/i) || findKey(/\bprojected\s*hr\b/i) || findKey(/\bhr\b/i);

    const v1 = toNum(k1b ? out[k1b] : null) || 0;
    const v2 = toNum(k2b ? out[k2b] : null) || 0;
    const v3 = toNum(k3b ? out[k3b] : null) || 0;
    const v4 = toNum(khr ? out[khr] : null) || 0;
    out["Projected H"] = v1 + v2 + v3 + v4;
    return out;
  });
}

function normalizeSimBB(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((r) => {
    const out = { ...r };
    const simKey =
      Object.keys(out).find((k) => k.toLowerCase().replace(/\s+/g, "") === "simbbmean") ||
      Object.keys(out).find((k) => k.toLowerCase().includes("sim") && k.toLowerCase().includes("bb") && k.toLowerCase().includes("mean"));

    if (simKey && out["Projected BB"] == null) {
      out["Projected BB"] = out[simKey];
    }
    return out;
  });
}

function mergePitcherTeamData(simRows, pitcherDataRows) {
  const dataRows = Array.isArray(pitcherDataRows) ? pitcherDataRows : [];
  const sim = Array.isArray(simRows) ? simRows : [];

  const byName = new Map();

  for (const row of dataRows) {
    const key = normalizeName(
      row?.Player ??
      row?.player ??
      row?.Name ??
      row?.name
    );
    if (!key) continue;
    byName.set(key, row);
  }

  return sim.map((row) => {
    const key = normalizeName(
      row?.Player ??
      row?.player ??
      row?.Name ??
      row?.name
    );

    const match = byName.get(key);
    if (!match) return row;

    return {
      ...match,
      ...row,
      Team:
        row?.Team ??
        row?.team ??
        row?.Tm ??
        row?.tm ??
        match?.Team ??
        match?.team ??
        match?.Tm ??
        match?.tm ??
        "",
    };
  });
}

function getColumns(rows) {
  if (!rows?.length) return [];
  return Object.keys(rows[0]);
}

function orderColumns(cols, playerKey) {
  const front = [playerKey];
  const set = new Set(cols);
  const head = front.filter((c) => set.has(c));
  const tail = cols.filter((c) => !head.includes(c));
  return [...head, ...tail];
}

function isOutcomeCol(col) {
  return /^P\(/.test(col) || col.toLowerCase().startsWith("p(");
}

function prettyOutcomeLabel(col) {
  const m = String(col || "").match(/^P\(\s*([A-Za-z0-9%]+)\s*(?:>=|≥)\s*(\d+)\s*\)$/i);
  if (!m) return col;
  let stat = String(m[1] || "").toUpperCase();
  const n = m[2];

  if (stat === "SO") stat = "K";
  if (stat === "BB%") stat = "BB";

  if (["1B", "2B", "3B"].includes(stat)) {
    return n === "1" ? `${stat}+` : `${n} ${stat}+`;
  }
  return `${n}${stat}+`;
}

function onlyGteOutcomeCols(cols) {
  return cols.filter(isOutcomeCol).filter((c) => c.includes(">=") || c.includes("≥"));
}

function hasAnyData(rows, col, playerKey) {
  for (const r of rows) {
    if (col === playerKey) continue;
    const v = r?.[col];
    if (v == null || v === "") continue;
    if (isNum(v)) {
      if (Math.abs(v) > 1e-12) return true;
    } else {
      return true;
    }
  }
  return false;
}

function defaultProjectionCols(cols) {
  const keep = [];
  for (const c of cols) {
    const lc = c.toLowerCase();
    const isProjected = lc.startsWith("projected ");
    const isDkfd =
      lc.startsWith("dk ") ||
      lc.startsWith("fd ") ||
      lc.includes("dk_floor") ||
      lc.includes("dk mean") ||
      lc.includes("ceiling") ||
      lc.includes("floor") ||
      lc.includes("std") ||
      lc.includes("stdev");

    if (lc.includes("projected bb") && lc.includes("sheet")) continue;
    if (isProjected || isDkfd || c === "Projected BB") keep.push(c);
  }
  if (!keep.length) {
    return cols.filter((c) => !(c.toLowerCase().includes("projected bb") && c.toLowerCase().includes("sheet")));
  }
  return Array.from(new Set(keep));
}

function metricBucketsForOutcomes(who, gteCols) {
  const buckets = [];
  const add = (key, label, matcher) => {
    const cols = gteCols.filter((c) => matcher(c));
    if (cols.length) buckets.push({ key, label, cols });
  };

  if (who === "batter") {
    add("H", "Hits", (c) => /P\(\s*H\b/i.test(c));
    add("1B", "1B", (c) => /P\(\s*1B\b/i.test(c));
    add("2B", "2B", (c) => /P\(\s*2B\b/i.test(c));
    add("3B", "3B", (c) => /P\(\s*3B\b/i.test(c));
    add("HR", "HR", (c) => /P\(\s*HR\b/i.test(c));
    add("RBI", "RBI", (c) => /P\(\s*RBI\b/i.test(c));
    add("R", "Runs", (c) => /P\(\s*R\b/i.test(c) && !/RBI/i.test(c));
    add("SB", "SB", (c) => /P\(\s*SB\b/i.test(c));
    add("BB", "BB", (c) => /P\(\s*BB\b/i.test(c));
    add("K", "K", (c) => /P\(\s*K\b/i.test(c));
  } else {
    add("K", "Strikeouts", (c) => /P\(\s*K\b/i.test(c));
    add("ER", "Earned Runs", (c) => /P\(\s*ER\b/i.test(c));
    add("H", "Hits Allowed", (c) => /P\(\s*H\b/i.test(c));
    add("BB", "Walks", (c) => /P\(\s*BB\b/i.test(c));
    add("HR", "HR Allowed", (c) => /P\(\s*HR\b/i.test(c));
    add("IP", "Innings", (c) => /P\(\s*IP\b/i.test(c));
    add("W", "Win", (c) => /P\(\s*W\b/i.test(c));
  }

  if (!buckets.length && gteCols.length) {
    buckets.push({ key: "all", label: "All", cols: gteCols });
  }

  return buckets;
}

function isAllZeroishRow(row, colsToCheck) {
  let sawNonZero = false;
  for (const c of colsToCheck) {
    const v = row?.[c];
    if (v == null || v === "") continue;
    if (isNum(v)) {
      if (Math.abs(v) > 1e-12) {
        sawNonZero = true;
        break;
      }
    } else {
      sawNonZero = true;
      break;
    }
  }
  return !sawNonZero;
}

/** =========================
 * REPORT HELPERS
 * ========================= */
function safeTop(rows, scoreKey, count = 5) {
  return [...(rows || [])]
    .filter((r) => isNum(toNum(r?.[scoreKey])))
    .sort((a, b) => (toNum(b?.[scoreKey]) || 0) - (toNum(a?.[scoreKey]) || 0))
    .slice(0, count);
}

function aggregateByTeam(rows, metrics) {
  const map = new Map();

  for (const row of rows || []) {
    const team = normalizeTeam(row?.Team || row?.team || row?.Tm || row?.tm || "");
    if (!team) continue;

    if (!map.has(team)) {
      const seed = { Team: team };
      for (const m of metrics) seed[m] = 0;
      map.set(team, seed);
    }

    const cur = map.get(team);
    for (const m of metrics) {
      cur[m] += toNum(row?.[m]) || 0;
    }
  }

  return Array.from(map.values());
}

function reportCardsForPlayers(kind, rows) {
  if (kind === "batter") {
    return [
      {
        key: "hr",
        title: "Highest HR%",
        subtitle: "Top probability to homer",
        scoreKey: "P(HR >= 1)",
        display: (r) => toPct(toNum(r["P(HR >= 1)"]), 1),
      },
      {
        key: "hit",
        title: "Highest H%",
        subtitle: "Top probability for 1+ hit",
        scoreKey: "P(H >= 1)",
        display: (r) => toPct(toNum(r["P(H >= 1)"]), 1),
      },
      {
        key: "double",
        title: "Highest 2B%",
        subtitle: "Top probability for 1+ double",
        scoreKey: "P(2B >= 1)",
        display: (r) => toPct(toNum(r["P(2B >= 1)"]), 1),
      },
      {
        key: "triple",
        title: "Highest 3B%",
        subtitle: "Top probability for 1+ triple",
        scoreKey: "P(3B >= 1)",
        display: (r) => toPct(toNum(r["P(3B >= 1)"]), 1),
      },
      {
        key: "run",
        title: "Highest R%",
        subtitle: "Top probability for 1+ run",
        scoreKey: "P(R >= 1)",
        display: (r) => toPct(toNum(r["P(R >= 1)"]), 1),
      },
      {
        key: "rbi",
        title: "Highest RBI%",
        subtitle: "Top probability for 1+ RBI",
        scoreKey: "P(RBI >= 1)",
        display: (r) => toPct(toNum(r["P(RBI >= 1)"]), 1),
      },
      {
        key: "sb",
        title: "Highest SB%",
        subtitle: "Top probability for 1+ stolen base",
        scoreKey: "P(SB >= 1)",
        display: (r) => toPct(toNum(r["P(SB >= 1)"]), 1),
      },
      {
        key: "dkmean",
        title: "Best DK Mean",
        subtitle: "Highest DraftKings mean",
        scoreKey: "DK Mean",
        display: (r) => fmt2(toNum(r["DK Mean"])),
      },
      {
        key: "fdmean",
        title: "Best FD Mean",
        subtitle: "Highest FanDuel mean",
        scoreKey: "FD Mean",
        display: (r) => fmt2(toNum(r["FD Mean"])),
      },
    ];
  }

  return [
    {
      key: "projk",
      title: "Projected Ks",
      subtitle: "Highest projected strikeouts",
      scoreKey: "Projected K",
      display: (r) => fmt2(toNum(r["Projected K"])),
    },
    {
      key: "projip",
      title: "Projected Innings",
      subtitle: "Highest projected innings pitched",
      scoreKey: "Projected IP",
      display: (r) => fmt2(toNum(r["Projected IP"])),
    },
    {
      key: "projhr",
      title: "Projected HR Allowed",
      subtitle: "Highest projected homers allowed",
      scoreKey: "Projected HR",
      display: (r) => fmt2(toNum(r["Projected HR"])),
    },
    {
      key: "projbb",
      title: "Projected Walks",
      subtitle: "Highest projected walks allowed",
      scoreKey: "Projected BB",
      display: (r) => fmt2(toNum(r["Projected BB"])),
    },
    {
      key: "hrAllowed",
      title: "Most Likely HR Allowed",
      subtitle: "Pitchers most likely to allow 1+ HR",
      scoreKey: "P(HR >= 1)",
      display: (r) => toPct(toNum(r["P(HR >= 1)"]), 1),
    },
    {
      key: "erAllowed",
      title: "Most Likely 3+ ER Allowed",
      subtitle: "Pitchers most likely to allow 3+ earned runs",
      scoreKey: "P(ER >= 3)",
      display: (r) => toPct(toNum(r["P(ER >= 3)"]), 1),
    },
    {
      key: "dkmean",
      title: "Best DK Mean",
      subtitle: "Highest DraftKings mean",
      scoreKey: "DK Mean",
      display: (r) => fmt2(toNum(r["DK Mean"])),
    },
    {
      key: "fdmean",
      title: "Best FD Mean",
      subtitle: "Highest FanDuel mean",
      scoreKey: "FD Mean",
      display: (r) => fmt2(toNum(r["FD Mean"])),
    },
  ];
}

function reportCardsForTeams(kind, batterRows, pitcherRows) {
  if (kind === "batter") {
    const teamRows = aggregateByTeam(batterRows, [
      "Projected HR",
      "Projected H",
      "Projected R",
      "Projected RBI",
      "Projected K",
      "Projected BB",
      "Projected SB",
      "DK Mean",
      "FD Mean",
    ]);

    return [
      {
        key: "team-hr",
        title: "Teams Projected Most HR",
        subtitle: "Summed projected batters HR",
        rows: safeTop(teamRows, "Projected HR", 5),
        display: (r) => fmt2(toNum(r["Projected HR"])),
      },
      {
        key: "team-hits",
        title: "Teams Projected Most Hits",
        subtitle: "Summed projected batters hits",
        rows: safeTop(teamRows, "Projected H", 5),
        display: (r) => fmt2(toNum(r["Projected H"])),
      },
      {
        key: "team-runs",
        title: "Teams Projected Most Runs",
        subtitle: "Summed projected batters runs",
        rows: safeTop(teamRows, "Projected R", 5),
        display: (r) => fmt2(toNum(r["Projected R"])),
      },
      {
        key: "team-rbi",
        title: "Teams Projected Most RBI",
        subtitle: "Summed projected batters RBI",
        rows: safeTop(teamRows, "Projected RBI", 5),
        display: (r) => fmt2(toNum(r["Projected RBI"])),
      },
      {
        key: "team-ks",
        title: "Teams Most Projected K",
        subtitle: "Summed projected hitter strikeouts",
        rows: safeTop(teamRows, "Projected K", 5),
        display: (r) => fmt2(toNum(r["Projected K"])),
      },
      {
        key: "team-sb",
        title: "Teams Projected Most SB",
        subtitle: "Summed projected steals",
        rows: safeTop(teamRows, "Projected SB", 5),
        display: (r) => fmt2(toNum(r["Projected SB"])),
      },
    ];
  }

  const teamRows = aggregateByTeam(pitcherRows, [
    "Projected K",
    "Projected ER",
    "Projected H",
    "Projected BB",
    "Projected HR",
    "Projected W",
    "DK Mean",
    "FD Mean",
  ]);

  return [
    {
      key: "pteam-k",
      title: "Pitching Teams Projected Most K",
      subtitle: "Summed projected pitcher strikeouts",
      rows: safeTop(teamRows, "Projected K", 5),
      display: (r) => fmt2(toNum(r["Projected K"])),
    },
    {
      key: "pteam-win",
      title: "Pitching Teams Best Win Equity",
      subtitle: "Summed projected pitcher wins",
      rows: safeTop(teamRows, "Projected W", 5),
      display: (r) => toPct(toNum(r["Projected W"]), 1),
    },
    {
      key: "pteam-hra",
      title: "Pitching Teams Most HR Allowed",
      subtitle: "Summed projected pitcher HR allowed",
      rows: safeTop(teamRows, "Projected HR", 5),
      display: (r) => fmt2(toNum(r["Projected HR"])),
    },
    {
      key: "pteam-era",
      title: "Pitching Teams Most ER Allowed",
      subtitle: "Summed projected pitcher ER allowed",
      rows: safeTop(teamRows, "Projected ER", 5),
      display: (r) => fmt2(toNum(r["Projected ER"])),
    },
    {
      key: "pteam-ha",
      title: "Pitching Teams Most Hits Allowed",
      subtitle: "Summed projected pitcher hits allowed",
      rows: safeTop(teamRows, "Projected H", 5),
      display: (r) => fmt2(toNum(r["Projected H"])),
    },
    {
      key: "pteam-bb",
      title: "Pitching Teams Most Walks Allowed",
      subtitle: "Summed projected pitcher walks",
      rows: safeTop(teamRows, "Projected BB", 5),
      display: (r) => fmt2(toNum(r["Projected BB"])),
    },
  ];
}

/** =========================
 * UI COMPONENTS
 * ========================= */
function Segmented({ value, onChange, options, className = "" }) {
  return (
    <div className={cx("flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 p-1", className)}>
      {options.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={cx(
            "px-4 py-2 text-xs rounded-full transition font-semibold",
            value === opt.key
              ? "bg-sky-500 text-slate-950 shadow-sm"
              : "text-slate-300 hover:bg-slate-800/80"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function InitialsAvatar({ name }) {
  return (
    <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[11px] font-bold text-slate-200 shrink-0">
      {initials(name)}
    </div>
  );
}

function PlayerChip({ player, team, src }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="relative shrink-0">
        {src ? (
          <img
            src={src}
            alt={player}
            className="w-10 h-10 rounded-full object-cover border border-slate-700 bg-slate-900"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const next = e.currentTarget.nextSibling;
              if (next) next.style.display = "flex";
            }}
          />
        ) : null}
        <div style={{ display: src ? "none" : "flex" }}>
          <InitialsAvatar name={player} />
        </div>

        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border border-slate-800 bg-slate-950 flex items-center justify-center overflow-hidden">
          <img
            src={teamLogo(team)}
            alt={team}
            className="w-4 h-4 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-100 truncate">{player}</div>
        <div className="text-[11px] text-slate-400 truncate">{team}</div>
      </div>
    </div>
  );
}

function TeamChip({ team }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 rounded-2xl border border-slate-700 bg-slate-900 flex items-center justify-center shrink-0">
        <img
          src={teamLogo(team)}
          alt={team}
          className="w-7 h-7 object-contain"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-100 truncate">{team}</div>
        <div className="text-[11px] text-slate-400">Team Report</div>
      </div>
    </div>
  );
}

function ReportCard({ title, subtitle, rows, entityType, scoreKey, display, playerKey, teamKey, headshotIndex }) {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-950/70 shadow-xl shadow-slate-950/40 p-4">
      <div className="mb-3">
        <div className="text-sm font-bold text-slate-100">{title}</div>
        <div className="text-[11px] text-slate-400">{subtitle}</div>
      </div>

      <div className="space-y-2">
        {(rows || []).map((r, idx) => {
          const player = r?.[playerKey];
          const team = r?.[teamKey] || r?.Team;
          const score = display ? display(r) : fmt2(toNum(r?.[scoreKey]));
          const headshotSrc =
            entityType === "player"
              ? findHeadshotSrc({ player, team, headshotIndex })
              : "";

          return (
            <div
              key={`${title}-${idx}-${player || team}`}
              className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/70 px-3 py-2.5 hover:bg-slate-900 transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-6 text-center text-xs font-bold text-sky-300 shrink-0">
                  {idx + 1}
                </div>

                {entityType === "player" ? (
                  <PlayerChip player={player} team={team} src={headshotSrc} />
                ) : (
                  <TeamChip team={team} />
                )}
              </div>

              <div className="text-right shrink-0">
                <div className="text-sm font-extrabold text-slate-50">{score}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** =========================
 * MAIN PAGE
 * ========================= */
export default function MlbSimulations() {
  const [pageTab, setPageTab] = useState("reports"); // reports | browser
  const [reportEntity, setReportEntity] = useState("players"); // players | teams
  const [reportWho, setReportWho] = useState("batter"); // batter | pitcher

  const [who, setWho] = useState("batter"); // browser tab
  const [view, setView] = useState("outcomes"); // browser tab
  const [oddsMode, setOddsMode] = useState("pct");

  const [data, setData] = useState({ batter: [], pitcher: [] });
  const [headshots, setHeadshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleByKey, setVisibleByKey] = useState({});
  const [metricTab, setMetricTab] = useState("");

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [bat, pit, pitData, hs] = await Promise.all([
          fetchJson(FILES.batter),
          fetchJson(FILES.pitcher),
          fetchJson(FILES.pitcherData),
          fetchOptionalJson(FILES.headshots),
        ]);
        if (dead) return;

        const batterRows = Array.isArray(bat) ? bat : [];
        const pitcherRows = Array.isArray(pit) ? pit : [];

        const mergedPitchers = mergePitcherTeamData(pitcherRows, pitData);

        setData({
          batter: addDerivedProjectedHits(normalizeSimBB(batterRows)),
          pitcher: normalizeSimBB(mergedPitchers),
        });
        setHeadshots(Array.isArray(hs) ? hs : []);
      } catch (e) {
        if (!dead) setErr(String(e?.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();

    return () => {
      dead = true;
    };
  }, []);

  const headshotIndex = useMemo(() => buildHeadshotIndex(headshots), [headshots]);

  /** =========================
   * REPORTS DATA
   * ========================= */
  const batterRowsClean = useMemo(() => {
    return (data.batter || []).filter((r) => {
      const player = String(r?.Player || r?.player || r?.Name || "").trim();
      if (!player) return false;
      return (toNum(r?.["Projected H"]) || 0) > 0;
    });
  }, [data.batter]);

  const pitcherRowsClean = useMemo(() => {
    return (data.pitcher || []).filter((r) => {
      const player = String(r?.Player || r?.player || r?.Name || "").trim();
      return !!player;
    });
  }, [data.pitcher]);

  const reportSearchRows = reportWho === "batter" ? batterRowsClean : pitcherRowsClean;

  const reportFilteredRows = useMemo(() => {
    const needle = q.trim();
    if (!needle) return reportSearchRows;
    return reportSearchRows.filter((r) => {
      const player = r?.Player || r?.player || r?.Name || "";
      const team = r?.Team || r?.team || "";
      return fuzzyIncludes(player, needle) || fuzzyIncludes(team, needle);
    });
  }, [reportSearchRows, q]);

  const reportPlayerCards = useMemo(() => {
    const sourceRows = reportFilteredRows;
    return reportCardsForPlayers(reportWho, sourceRows).map((card) => ({
      ...card,
      rows: safeTop(sourceRows, card.scoreKey, 5),
    }));
  }, [reportFilteredRows, reportWho]);

  const reportTeamCards = useMemo(() => {
    return reportCardsForTeams(reportWho, reportFilteredRows, reportFilteredRows);
  }, [reportFilteredRows, reportWho]);

  /** =========================
   * BROWSER / TABLE DATA
   * ========================= */
  const rawRows = data[who] || [];
  const playerKey = useMemo(() => pickPlayerKey(rawRows), [rawRows]);
  const teamKey = useMemo(() => guessTeamKeyFromRow(rawRows?.[0]), [rawRows]);

  const cleanedRows = useMemo(() => {
    const cols = rawRows?.length ? Object.keys(rawRows[0]) : [];
    const checkCols = cols.filter((c) => c !== playerKey);

    const pHit1Key =
      who !== "batter"
        ? null
        : cols.find((k) => /^P\(\s*H\s*(?:>=|≥)\s*1\s*\)$/i.test(String(k))) ||
          cols.find((k) => /^P\(\s*HITS?\s*(?:>=|≥)\s*1\s*\)$/i.test(String(k)));

    return (rawRows || [])
      .filter((r) => String(r?.[playerKey] ?? "").trim().length > 0)
      .filter((r) => {
        if (who !== "batter") return true;
        const h = toNum(r?.["Projected H"]) || 0;
        return h > 0;
      })
      .filter((r) => {
        if (who !== "batter") return true;
        if (!pHit1Key) return true;
        const v = toNum(r?.[pHit1Key]);
        if (!isNum(v)) return true;
        return v > 0;
      })
      .filter((r) => !isAllZeroishRow(r, checkCols));
  }, [rawRows, playerKey, who]);

  const allColumns = useMemo(() => {
    const cols = getColumns(cleanedRows);
    return orderColumns(cols, playerKey);
  }, [cleanedRows, playerKey]);

  const viewColumns = useMemo(() => {
    if (!allColumns.length) return [];
    if (view === "outcomes") {
      const gte = onlyGteOutcomeCols(allColumns);
      return [playerKey, ...gte];
    }
    const projCols = defaultProjectionCols(allColumns);
    return orderColumns(projCols.includes(playerKey) ? projCols : [playerKey, ...projCols], playerKey)
      .filter((c) => !(c.toLowerCase().includes("projected bb") && c.toLowerCase().includes("sheet")));
  }, [allColumns, view, playerKey]);

  const outcomeBuckets = useMemo(() => {
    if (view !== "outcomes") return [];
    const gteCols = viewColumns.filter((c) => c !== playerKey);
    return metricBucketsForOutcomes(who, gteCols);
  }, [view, who, viewColumns, playerKey]);

  useEffect(() => {
    if (view !== "outcomes") return;
    if (!outcomeBuckets.length) {
      setMetricTab("");
      return;
    }
    setMetricTab((prev) => (outcomeBuckets.some((b) => b.key === prev) ? prev : outcomeBuckets[0].key));
  }, [view, outcomeBuckets]);

  const baseColumns = useMemo(() => {
    if (!viewColumns.length) return [];
    if (view === "outcomes" && outcomeBuckets.length && metricTab) {
      const bucket = outcomeBuckets.find((b) => b.key === metricTab);
      return bucket ? [playerKey, ...bucket.cols] : viewColumns;
    }
    return viewColumns;
  }, [viewColumns, view, outcomeBuckets, metricTab, playerKey]);

  const columnsWithData = useMemo(() => {
    return baseColumns.filter((c) => c === playerKey || hasAnyData(cleanedRows, c, playerKey));
  }, [baseColumns, cleanedRows, playerKey]);

  const visKey = `${who}:${view}:${view === "outcomes" ? metricTab : "proj"}`;

  useEffect(() => {
    if (!columnsWithData.length) return;
    setVisibleByKey((prev) => {
      if (prev[visKey]?.length) return prev;
      return { ...prev, [visKey]: columnsWithData };
    });

    setSort((p) => {
      const defKey = columnsWithData.includes(playerKey) ? playerKey : columnsWithData[0] || "";
      return p.key ? p : { key: defKey, dir: "asc" };
    });
  }, [visKey, columnsWithData, playerKey]);

  const visibleColumns = visibleByKey[visKey] || columnsWithData;

  const rowsForView = useMemo(() => {
    if (view !== "outcomes") return cleanedRows;
    const statCols = (visibleColumns || []).filter((c) => c !== playerKey);
    if (!statCols.length) return cleanedRows;

    return (cleanedRows || []).filter((r) =>
      statCols.some((c) => {
        const n = toNum(r?.[c]);
        return n != null && Number.isFinite(n) && n > 0;
      })
    );
  }, [cleanedRows, view, visibleColumns, playerKey]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rowsForView;
    return rowsForView.filter((r) =>
      visibleColumns.some((c) => String(r?.[c] ?? "").toLowerCase().includes(needle))
    );
  }, [rowsForView, q, visibleColumns]);

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    if (!key) return filtered;
    const sign = dir === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      if (isNum(av) && isNum(bv)) return (av - bv) * sign;

      const an = typeof av === "string" ? Number(av) : NaN;
      const bn = typeof bv === "string" ? Number(bv) : NaN;
      if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * sign;

      return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
    });
  }, [filtered, sort]);

  function onHeaderClick(col) {
    setSort((p) => (p.key === col ? { key: col, dir: p.dir === "asc" ? "desc" : "asc" } : { key: col, dir: "asc" }));
  }

  function toggleColumn(col) {
    setVisibleByKey((prev) => {
      const cur = prev[visKey] || columnsWithData;
      const next = cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col];
      const enforced = next.includes(playerKey) ? next : [playerKey, ...next];
      return { ...prev, [visKey]: enforced };
    });
  }

  function selectAll() {
    setVisibleByKey((prev) => ({ ...prev, [visKey]: columnsWithData }));
  }

  function clearAll() {
    setVisibleByKey((prev) => ({ ...prev, [visKey]: [playerKey] }));
  }

  function displayCell(col, v) {
    if (view === "outcomes" && col !== playerKey) {
      if (!isNum(v) || v <= 0) return "";
      const p = v <= 1.0000001 ? v : v / 100;
      return oddsMode === "odds" ? probToAmericanOdds(p) : toPct(p);
    }

    if (view === "projections" && col !== playerKey && isNum(v)) return fmt2(v);
    return fmt1(v);
  }

  const title = "MLB Simulations";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617] text-slate-100 px-3 py-6 md:px-6">
      <div className="max-w-7xl mx-auto rounded-2xl border border-slate-800/70 bg-slate-950/80 shadow-2xl backdrop-blur-sm p-4 md:p-6 space-y-4">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-50">{title}</h1>
            <div className="text-sm text-slate-400 mt-1">
              Daily report boards + full batter and pitcher simulation browser.
            </div>
          </div>

          {pageTab === "browser" && view === "outcomes" ? (
            <div className="ml-auto flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/80 p-1">
              {[
                { key: "pct", label: "%" },
                { key: "odds", label: "Odds" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setOddsMode(opt.key)}
                  className={cx(
                    "px-4 py-2 text-xs rounded-full transition font-semibold",
                    oddsMode === opt.key ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-300 hover:bg-slate-800/80"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* TOP NAV */}
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={pageTab}
            onChange={setPageTab}
            options={[
              { key: "reports", label: "Daily Reports" },
              { key: "browser", label: "Simulations" },
            ]}
          />

          {pageTab === "reports" ? (
            <>
              <Segmented
                value={reportEntity}
                onChange={setReportEntity}
                options={[
                  { key: "players", label: "Players" },
                  { key: "teams", label: "Teams" },
                ]}
              />
              <Segmented
                value={reportWho}
                onChange={setReportWho}
                options={[
                  { key: "batter", label: "Batters" },
                  { key: "pitcher", label: "Pitchers" },
                ]}
              />
            </>
          ) : (
            <>
              <Segmented
                value={who}
                onChange={setWho}
                options={[
                  { key: "batter", label: "Batters" },
                  { key: "pitcher", label: "Pitchers" },
                ]}
              />
              <Segmented
                value={view}
                onChange={setView}
                options={[
                  { key: "outcomes", label: "Outcomes" },
                  { key: "projections", label: "Projections" },
                ]}
              />

              {view === "outcomes" && outcomeBuckets.length ? (
                <div className="flex flex-wrap items-center gap-2 ml-2">
                  {outcomeBuckets.map((b) => (
                    <button
                      key={b.key}
                      onClick={() => setMetricTab(b.key)}
                      className={cx(
                        "px-3 py-1.5 rounded-full border text-xs font-semibold transition",
                        metricTab === b.key
                          ? "bg-sky-500 text-slate-950 border-sky-400 shadow-sm"
                          : "bg-slate-900/70 border-slate-700 text-slate-200 hover:bg-slate-800"
                      )}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* CONTROLS */}
        <div className="flex items-center gap-3 flex-wrap">
          <input
            className="border border-slate-700 rounded-md px-3 py-2 text-xs w-full sm:w-[520px] bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            placeholder={
              pageTab === "reports"
                ? "Search player or team..."
                : "Search player / any column..."
            }
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          {pageTab === "reports" ? (
            <div className="text-[11px] text-slate-400">
              {reportEntity === "players"
                ? `Player pool: ${reportFilteredRows.length.toLocaleString()}`
                : `Teams shown in report cards`}
            </div>
          ) : (
            <div className="text-[11px] text-slate-400">
              Rows: {sorted.length.toLocaleString()}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            {pageTab === "browser" ? (
              <>
                <button
                  className="px-3 py-2 rounded-md border border-slate-700 bg-slate-900/80 text-xs font-semibold text-slate-100 hover:bg-slate-800 transition"
                  onClick={() => setColumnsOpen((v) => !v)}
                >
                  Columns
                </button>
                <button
                  className="px-3 py-2 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 transition border border-sky-400 shadow-sm"
                  onClick={() =>
                    exportToCsv(
                      `MLB_${who}_${view}${view === "outcomes" ? "_" + metricTab : ""}.csv`,
                      sorted,
                      visibleColumns
                    )
                  }
                >
                  Export CSV
                </button>
              </>
            ) : null}
          </div>
        </div>

        {err ? <div className="text-sm text-rose-400">{err}</div> : null}

        {/* REPORTS */}
        {pageTab === "reports" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-slate-800/80 bg-slate-950/70 shadow-xl shadow-slate-950/40 p-4 md:col-span-3 xl:col-span-4">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div>
                    <div className="text-lg font-bold text-slate-100">
                      {reportEntity === "players" ? "Daily Player Reports" : "Daily Team Reports"}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {reportWho === "batter"
                        ? reportEntity === "players"
                          ? "Top batter probabilities and projected scoring spots."
                          : "Top team-level offense spots from batter simulations."
                        : reportEntity === "players"
                        ? "Top pitcher strikeout, win, and damage-allowed spots."
                        : "Top team-level pitching spots from pitcher simulations."}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-slate-400">
                    <span className="px-2 py-1 rounded-full border border-slate-700 bg-slate-900/80">
                      {reportWho === "batter" ? "Batters" : "Pitchers"}
                    </span>
                    <span className="px-2 py-1 rounded-full border border-slate-700 bg-slate-900/80">
                      {reportEntity === "players" ? "Players" : "Teams"}
                    </span>
                  </div>
                </div>
              </div>

              {(reportEntity === "players" ? reportPlayerCards : reportTeamCards).map((card) => (
                <ReportCard
                  key={card.key}
                  title={card.title}
                  subtitle={card.subtitle}
                  rows={card.rows}
                  entityType={reportEntity === "players" ? "player" : "team"}
                  scoreKey={card.scoreKey || "Value"}
                  display={card.display}
                  playerKey="Player"
                  teamKey="Team"
                  headshotIndex={headshotIndex}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* COLUMN PICKER */}
            {columnsOpen ? (
              <div className="rounded-2xl border border-slate-700 bg-slate-950 shadow-xl shadow-slate-900/60 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <div className="text-xs text-slate-300">
                    Showing <span className="font-semibold text-slate-100">{visibleColumns.length}</span> /{" "}
                    <span className="font-semibold text-slate-100">{columnsWithData.length}</span> columns
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
                      onClick={selectAll}
                    >
                      Select all
                    </button>
                    <button
                      className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
                      onClick={clearAll}
                    >
                      Clear
                    </button>
                    <button
                      className="text-xs px-2 py-1 text-slate-300 hover:text-slate-100 hover:underline"
                      onClick={() => setColumnsOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 max-h-72 overflow-auto pr-2">
                  {columnsWithData.map((c) => {
                    const checked = visibleColumns.includes(c);
                    return (
                      <label
                        key={c}
                        className="flex items-start gap-2 text-[13px] px-1 py-1 rounded hover:bg-slate-800/70 cursor-pointer text-slate-100"
                        title={c}
                      >
                        <input
                          type="checkbox"
                          className="accent-sky-500 mt-0.5 w-3.5 h-3.5 flex-shrink-0"
                          checked={checked}
                          disabled={c === playerKey}
                          onChange={() => toggleColumn(c)}
                        />
                        <span className="leading-tight whitespace-nowrap">
                          {view === "outcomes" && c !== playerKey ? prettyOutcomeLabel(c) : c}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* TABLE */}
            <div className="border border-slate-800 rounded-2xl shadow-xl shadow-slate-900/70 overflow-auto h-[70vh] bg-slate-950/60 backdrop-blur">
              <table className="min-w-max text-xs leading-tight w-full">
                <thead className="bg-slate-900 sticky top-0 z-30">
                  <tr>
                    {visibleColumns.map((c) => {
                      const isSticky = c === playerKey;
                      const isSorted = sort.key === c;
                      return (
                        <th
                          key={c}
                          className={cx(
                            "text-center px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none text-slate-200",
                            isSticky && "sticky left-0 z-40 bg-slate-900 border-r border-slate-800"
                          )}
                          onClick={() => onHeaderClick(c)}
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>{view === "outcomes" && c !== playerKey ? prettyOutcomeLabel(c) : c}</span>
                            {isSorted ? (
                              <span className="text-[9px] opacity-60">{sort.dir === "asc" ? "▲" : "▼"}</span>
                            ) : null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                        Loading…
                      </td>
                    </tr>
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                        No rows.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((r, i) => {
                      const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
                      const player = r?.[playerKey];
                      const team = teamKey ? r?.[teamKey] : r?.Team;
                      const headshotSrc = findHeadshotSrc({
                        player,
                        team,
                        headshotIndex,
                      });

                      return (
                        <tr key={i} className={rowBg}>
                          {visibleColumns.map((c) => {
                            const isSticky = c === playerKey;
                            const val = r?.[c];

                            return (
                              <td
                                key={c}
                                className={cx(
                                  "px-2 py-1 whitespace-nowrap text-center text-slate-100",
                                  c === playerKey && "font-semibold",
                                  isSticky && `sticky left-0 z-20 ${rowBg} border-r border-slate-800`
                                )}
                              >
                                {c === playerKey ? (
                                  <div className="flex items-center justify-start gap-3 pl-2 min-w-[280px]">
                                    <div className="relative shrink-0">
                                      {headshotSrc ? (
                                        <img
                                          src={headshotSrc}
                                          alt={player}
                                          className="w-8 h-8 rounded-full object-cover border border-slate-700 bg-slate-900"
                                          onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                            const next = e.currentTarget.nextSibling;
                                            if (next) next.style.display = "flex";
                                          }}
                                        />
                                      ) : null}

                                      <div style={{ display: headshotSrc ? "none" : "flex" }}>
                                        <InitialsAvatar name={player} />
                                      </div>

                                      <div className="absolute -bottom-1 -right-1 w-4.5 h-4.5 rounded-full border border-slate-900 bg-slate-950 flex items-center justify-center overflow-hidden">
                                        <img
                                          src={teamLogo(team)}
                                          alt={team}
                                          className="w-3.5 h-3.5 object-contain"
                                          onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                          }}
                                        />
                                      </div>
                                    </div>

                                    <div className="flex flex-col items-start min-w-0">
                                      <span className="text-left truncate">{displayCell(c, val)}</span>
                                      <span className="text-[10px] text-slate-500">{team}</span>
                                    </div>
                                  </div>
                                ) : (
                                  displayCell(c, val)
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}